import { IContentControlVideoSource } from './i-content-control-video-source';
import { FormatNumber } from '../common/ui-utils';

export class ContentControlVideoSource implements IContentControlVideoSource {
  private _videoFps = 0;
  private _oneFrameSecs = 0;

  constructor(private readonly _element: HTMLVideoElement) {}


  get currentTimeSecs(): number { return this._element.currentTime; }

  get rawCurrentTimeSecs(): number { return this._element.currentTime; }
  set rawCurrentTimeSecs(value: number) { this._element.currentTime = value; }

  get videoFps(): number { return this._videoFps; }
  get oneFrameSecs(): number { return this._oneFrameSecs; }

  public setupFrameRate(fps: number): void {
    this._videoFps = fps;
    this._oneFrameSecs = +(1 / fps).toPrecision(12);
  }

  public currentPlayTimeAsFrameIndex(timeSecs?: number): number {
    timeSecs = timeSecs ?? this.currentTimeSecs;
    const fn = timeSecs * this._videoFps;
    let frameIndex = Math.floor(fn);
    if (+(fn - frameIndex).toPrecision(12) >= this._oneFrameSecs) {
      frameIndex++;
    }
    return frameIndex;
  }

  public currentPlayTimeAsClosestFrameSecs(timeSecs?: number): number {
    if (timeSecs === 0) return 0;
    const frameIndex = this.currentPlayTimeAsFrameIndex(timeSecs);
    return frameIndex / this._videoFps;
  }

  public currentPlayTimeAsClosestFrameMs(timeSecs?: number): number {
    if (timeSecs === 0) return 0;
    const frameIndex = this.currentPlayTimeAsFrameIndex(timeSecs);
    return FormatNumber((frameIndex / this._videoFps) * 1000);
  }


  get muted(): boolean { return this._element.muted; }
  set muted(value: boolean) { this._element.muted = value; }

  get playbackRate(): number { return this._element.playbackRate; }
  set playbackRate(value: number) { this._element.playbackRate = value; }

  get widthPx(): number { return this._element.width; }
  set widthPx(value: number) { this._element.width = value; }

  get heightPx(): number { return this._element.height; }
  set heightPx(value: number) { this._element.height = value; }

  get durationSecs(): number { return this._element.duration; }
  get paused(): boolean { return this._element.paused; }
  get readyState(): number { return this._element.readyState; }

  public play(): Promise<void> { return this._element.play(); }
  public pause(): void { this._element.pause(); }
  public load(): void { this._element.load(); }
  public seekToVideoPlayerRelativeTimeInSecs(seekTimeSecs: number): void {
    const SEEK_EPSILON = 0.00001;
    const clamped = Math.min(seekTimeSecs + SEEK_EPSILON, this.durationSecs - SEEK_EPSILON);
    this._element.currentTime = clamped;
  }

  public addEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions): void {
    this._element.addEventListener(type, listener, options);
  }

  public removeEventListener(type: string, listener: EventListenerOrEventListenerObject): void {
    this._element.removeEventListener(type, listener);
  }

  public addSource(url: string): void {
    const source = document.createElement('source');
    source.src = url;
    this._element.appendChild(source);
  }

  public addAudioTrack(url: string, language: string): void {
    const audio = new Audio();
    audio.src = url;
    audio.lang = language;
    audio.crossOrigin = 'anonymous';
    this._element.appendChild(audio);
  }

  public clearSource(): void {
    this._element.removeAttribute('src');
  }
  getTexImageSource(): TexImageSource {
    return this._element as TexImageSource;
  }
}
